import { useCallback, useEffect, useRef, useState } from "react";

export function scrollToCategorySection(
  sectionId: string | null,
  options?: { behavior?: ScrollBehavior }
): void {
  const behavior = options?.behavior ?? "smooth";
  const targetId = sectionId ?? "catalog-top";
  document.getElementById(targetId)?.scrollIntoView({ behavior, block: "start" });
}

export function useCategoryScrollSpy(sectionIds: string[]) {
  const [activeSectionId, setActiveSectionId] = useState<string | null>(
    sectionIds[0] ?? null
  );
  const programmaticScrollRef = useRef(false);
  const scrollTimeoutRef = useRef<number | null>(null);

  const scrollToSection = useCallback(
    (sectionId: string | null) => {
      programmaticScrollRef.current = true;
      if (scrollTimeoutRef.current !== null) {
        window.clearTimeout(scrollTimeoutRef.current);
      }
      setActiveSectionId(sectionId ?? sectionIds[0] ?? null);
      scrollToCategorySection(sectionId);

      scrollTimeoutRef.current = window.setTimeout(() => {
        programmaticScrollRef.current = false;
        scrollTimeoutRef.current = null;
      }, 600);
    },
    [sectionIds]
  );

  useEffect(() => {
    return () => {
      if (scrollTimeoutRef.current !== null) {
        window.clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (sectionIds.length === 0) {
      setActiveSectionId(null);
      return;
    }

    const elements = sectionIds
      .map((id) => document.getElementById(id))
      .filter((el): el is HTMLElement => el !== null);

    if (elements.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (programmaticScrollRef.current) return;

        const visible = entries.filter((e) => e.isIntersecting);
        if (visible.length === 0) return;

        const topmost = visible.reduce((best, entry) =>
          entry.boundingClientRect.top < best.boundingClientRect.top ? entry : best
        );

        setActiveSectionId(topmost.target.id);
      },
      { rootMargin: "-15% 0px -55% 0px", threshold: [0, 0.15, 0.5] }
    );

    elements.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [sectionIds.join("|")]);

  return { activeSectionId, scrollToSection };
}
